import React, { FC, startTransition, useEffect } from "react";
import * as styles from "./DefaultSubscribeForm.module.scss";
import { FormProvider, UseFormReturn } from "react-hook-form";
import {
  Button,
  ModalFooter,
  FormGenericErrorMessage,
  FormRow
} from "@silversea-ssc/ui-components-react";
import { FormRequestType, LeadAPIRequest } from "src/ssc-api/leadAPIClient";
import { Geolocation } from "src/utils/localization/geolocation/geolocation";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import AsyncForm from "components/Form/AsyncForm";

import { Country, useGetCountries, useGetStatesForCountry } from "./utils";
import { LeadFormData } from "./types";
import DisclaimerMessage from "./DisclaimerMessage";
import { stringPresent } from "src/utils/strings";
import {
  HiddenTrackingFields,
  InputFormAddress1,
  InputFormCity,
  InputFormCountry,
  InputFormDescription,
  InputFormEmail,
  InputFormIsNotAgent,
  InputFormNameFirst,
  InputFormNameLast,
  InputFormState,
  InputFormTitle,
  InputFormWorkingWithAgent,
  InputFormZip
} from "./fields";
import { CruiseSuiteFormProps, useLeadForm } from "./useLeadForm";
import RequiredFields from "./RequiredFields";
import clsx from "clsx";
import { FormDisclaimerFragment } from "types/graphql-types";
import { useLingui } from "@lingui/react/macro";

const PhoneInputControl = React.lazy(
  () => import("components/PhoneInputControl")
);
export interface DefaultSubscribeFormProps {
  formName: string;
  email?: string;
  onSubmitSuccess: (
    formData: LeadFormData,
    requestData: LeadAPIRequest
  ) => void;
  requestSource: string;
  requestType: FormRequestType;
  brochureCode?: string;
  askImNotTraveAgent: boolean;
  askWorkWithAgent: boolean;
  askPhone: boolean;
  phoneIsRequired?: boolean;
  askAddress: boolean;
  askDescription?: boolean;
  enableSubscribeEmail?: boolean;
  dataContentName?: string;
  cruiseSuiteInfo?: CruiseSuiteFormProps;
  submitButtonLabel?: string;
  className?: string;
  formDisclaimerContent: FormDisclaimerFragment | undefined;
}

interface UseCountryStateReturn {
  countries: Country[];
  states: string[];
}
export const useCountryState = (
  enable: boolean,
  geolocation: Geolocation,
  formMethods: UseFormReturn<LeadFormData, object>
): UseCountryStateReturn => {
  const countries = useGetCountries(enable);
  const { setValue, watch, getValues } = formMethods;
  const countryCodeISO3 = geolocation?.countryCodeISO3;
  const countriesLength = countries.length;
  const selectedCountry = watch("Country");
  const states = useGetStatesForCountry(enable ? selectedCountry : undefined);

  useEffect(() => {
    if (!enable || countriesLength == 0) return;
    const selectedCountry = getValues("Country");
    if (stringPresent(selectedCountry)) return;
    startTransition(() => {
      setValue("Country", countryCodeISO3 || "", {
        shouldDirty: true,
        shouldValidate: true
      }); // FORCE update selected country
    });
  }, [countriesLength, enable, countryCodeISO3, setValue, getValues]);

  return { countries, states };
};

const DefaultSubscribeForm: FC<
  React.PropsWithChildren<DefaultSubscribeFormProps>
> = ({
  email,
  formName,
  onSubmitSuccess,
  requestSource,
  requestType,
  brochureCode,
  askImNotTraveAgent,
  askPhone,
  phoneIsRequired = false,
  dataContentName,
  enableSubscribeEmail = true,
  askDescription = false,
  askWorkWithAgent,
  askAddress = false,
  cruiseSuiteInfo = undefined,
  submitButtonLabel,
  className,
  formDisclaimerContent
}) => {
  const geolocation = useGeolocation();
  const { i18n, t } = useLingui();

  const { formMethods, onSubmit } = useLeadForm({
    askImNotTraveAgent,
    askWorkWithAgent,
    onSubmitSuccess,
    requestSource,
    requestType,
    brochureCode,
    cruiseSuiteInfo,
    email,
    enableSubscribeEmail,
    formName
  });

  const { formState, register } = formMethods;
  const { countries, states } = useCountryState(
    askAddress,
    geolocation,
    formMethods
  );

  return (
    <FormProvider {...formMethods}>
      <AsyncForm
        name={formName}
        onSubmit={onSubmit}
        className={clsx(styles.form, className)}
        dataContentName={dataContentName}
      >
        <HiddenTrackingFields
          register={register}
          brochureCode={!!brochureCode}
          enableSubscribeEmail={!!enableSubscribeEmail}
          cruiseSuiteInfo={!!cruiseSuiteInfo}
        />

        <FormRow columns="2">
          <InputFormTitle register={register} />
        </FormRow>
        <FormRow columns="2">
          <InputFormNameFirst register={register} />
          <InputFormNameLast register={register} />
        </FormRow>

        {askAddress && (
          <>
            <FormRow columns="2">
              <InputFormCountry register={register} countries={countries} />
            </FormRow>

            <InputFormAddress1 register={register} />
            <InputFormZip register={register} />
            <InputFormCity register={register} />
            <InputFormState register={register} states={states} />
          </>
        )}

        <InputFormEmail register={register} />

        {askPhone && (
          <React.Suspense fallback={<div></div>}>
            <PhoneInputControl
              name="Phone"
              defaultValue=""
              id="subscribe-modal-phone"
              required={phoneIsRequired}
            />
          </React.Suspense>
        )}

        {askDescription && <InputFormDescription register={register} />}

        <RequiredFields />
        {askImNotTraveAgent && <InputFormIsNotAgent register={register} />}
        {askWorkWithAgent && (
          <div className={styles.extraFieldsContainer}>
            <InputFormWorkingWithAgent register={register} />
          </div>
        )}

        <div className={styles.disclaimerText}>
          <DisclaimerMessage content={formDisclaimerContent} />
        </div>

        <ModalFooter>
          {!formState.isValid && formState.isSubmitted && (
            <FormGenericErrorMessage message="One or more fields have an error" />
          )}
          <Button
            className={styles.bodyFooterButton}
            renderAs="button"
            type="submit"
            loading={formState.isSubmitting}
            //disabled={formState.isSubmitting}
            data-testid="submit"
            dataContentPiece="submit_button"
            variant="accent"
            noWrap
          >
            {submitButtonLabel || i18n._(t`SUBMIT`)}
          </Button>
        </ModalFooter>
      </AsyncForm>
    </FormProvider>
  );
};

export default DefaultSubscribeForm;
